import React, { useEffect, useRef, useState } from "react";
import { notify } from "../Notification";
import usePlayer, { player } from "../../hooks/usePlayer";
import { Cover } from "./Library";
import { MORE_BY } from "../../data/spotify";
import drake from "../../assets/images/spotify/drake.jpg";
import {
  CaretDownIcon,
  ClockIcon,
  DownloadLargeIcon,
  ListIcon,
  MoreLargeIcon,
  PauseIcon,
  PauseLargeIcon,
  PlayIcon,
  PlayLargeIcon,
  SavedIcon,
  SavedLargeIcon,
  ShuffleLargeIcon,
  VideoIcon,
} from "./icons";

// 5:07, as Spotify writes a song's length.
export const formatDuration = (seconds) =>
  `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, "0")}`;

// The equalizer Spotify shows on the song that's playing.
const Equalizer = () => (
  <span className="sp-eq" aria-label="Playing">
    <i />
    <i />
    <i />
    <i />
  </span>
);

// A song's row: its number (a play button on hover, an equalizer while it plays), title and
// artists, plays, whether it's saved, and length. Double-click plays it.
const TrackRow = ({ track, index, current, playing, selected, showPlays, onSelect, onPlay }) => (
  <div
    role="row"
    className={`sp-track ${current ? "is-current" : ""} ${selected ? "is-selected" : ""}`}
    onClick={onSelect}
    onDoubleClick={onPlay}
  >
    <span className="sp-track-index" role="cell">
      {current && playing ? <Equalizer /> : <span className="sp-track-number">{index + 1}</span>}
      <button
        type="button"
        className="sp-track-play"
        onClick={(e) => {
          e.stopPropagation();
          onPlay();
        }}
        aria-label={current && playing ? `Pause ${track.title}` : `Play ${track.title}`}
      >
        {current && playing ? <PauseIcon /> : <PlayIcon />}
      </button>
    </span>
    <span className="sp-track-main" role="cell">
      <span className="sp-track-title">{track.title}</span>
      <span className="sp-track-sub">
        {track.explicit && (
          <span className="sp-explicit" aria-label="Explicit">
            E
          </span>
        )}
        {track.video && (
          <>
            <VideoIcon className="sp-video-icon" aria-hidden="true" />
            Music video •{" "}
          </>
        )}
        {track.artists.join(", ")}
      </span>
    </span>
    {showPlays && (
      <span className="sp-track-plays" role="cell">
        {track.plays ? track.plays.toLocaleString("en-US") : ""}
      </span>
    )}
    <span className="sp-track-end" role="cell">
      {track.saved && <SavedIcon className="sp-saved" aria-label="Saved" />}
      <span className="sp-track-duration">{formatDuration(track.duration)}</span>
    </span>
  </div>
);

// An album or playlist: its cover, name and details over its color, then the play button and the
// songs, then (for an album) its release and more by the artist. Scrolled down, a bar with the
// play button and name stays at the top, with the column headings under it. Spotify's own actions
// (download, more) say they're Spotify's.
const PlaylistPage = ({ list, scroller }) => {
  const { playlist, index: playingIndex, playing, shuffle } = usePlayer();
  const [selected, setSelected] = useState(null);
  const [compact, setCompact] = useState(false);
  const [headStuck, setHeadStuck] = useState(false);
  const actions = useRef(null);
  const head = useRef(null);

  useEffect(() => {
    const el = scroller.current;
    if (!el) return undefined;
    const onScroll = () => {
      const top = el.getBoundingClientRect().top + 64;
      setCompact(actions.current.getBoundingClientRect().bottom <= top);
      setHeadStuck(head.current.getBoundingClientRect().top <= top + 0.5);
    };
    onScroll();
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [scroller]);
  const isPlayingFrom = playlist.id === list.id;
  const showPlays = list.tracks.some((t) => t.plays);
  const canvas = list.tracks.find((t) => t.canvas);

  const playList = () => (isPlayingFrom ? player.toggle() : player.play(list.id, 0));
  const playTrack = (i) => (isPlayingFrom && playingIndex === i ? player.toggle() : player.play(list.id, i));
  const spotifyOnly = (what) => notify(`${what} works in Spotify itself, not in this replica.`);

  return (
    <div className="sp-page" style={{ "--sp-color": list.color }}>
      <div className={`sp-sticky-bar ${compact ? "is-shown" : ""}`} aria-hidden={!compact}>
        <button
          type="button"
          className="sp-big-play sp-big-play--small"
          onClick={playList}
          tabIndex={compact ? 0 : -1}
          aria-label={isPlayingFrom && playing ? `Pause ${list.name}` : `Play ${list.name}`}
        >
          {isPlayingFrom && playing ? <PauseLargeIcon /> : <PlayLargeIcon />}
        </button>
        <span className="sp-sticky-title">{list.name}</span>
      </div>
      <header className="sp-hero">
        <Cover item={{ image: list.cover }} className="sp-hero-cover" />
        <div className="sp-hero-text">
          <span className="sp-hero-kind">{list.kind}</span>
          <h1 className="sp-hero-title" style={{ "--chars": Math.max(6, list.name.length) }}>
            {list.name}
          </h1>
          <p className="sp-hero-meta">
            {list.artist ? (
              <>
                <img src={drake} alt="" className="sp-hero-avatar" draggable={false} />
                <b>{list.artist}</b> • {list.year} • {list.length}
              </>
            ) : (
              <>
                <b>{list.owner}</b> • {list.tracks.length} songs
              </>
            )}
          </p>
        </div>
      </header>

      <div className="sp-page-body">
        <div className="sp-actions" ref={actions}>
          <button
            type="button"
            className="sp-big-play"
            onClick={playList}
            aria-label={isPlayingFrom && playing ? `Pause ${list.name}` : `Play ${list.name}`}
          >
            {isPlayingFrom && playing ? <PauseLargeIcon /> : <PlayLargeIcon />}
          </button>
          {canvas && (
            <span className="sp-canvas-thumb" aria-hidden="true">
              <img src={canvas.canvas} alt="" draggable={false} />
            </span>
          )}
          <button
            type="button"
            className={`sp-action ${shuffle ? "is-on" : ""}`}
            onClick={player.toggleShuffle}
            aria-label={shuffle ? "Disable shuffle" : "Enable shuffle"}
            aria-pressed={shuffle}
          >
            <ShuffleLargeIcon />
          </button>
          {list.artist && (
            <span className="sp-action sp-action--saved" aria-label="Saved to Your Library">
              <SavedLargeIcon />
            </span>
          )}
          <button type="button" className="sp-action" onClick={() => spotifyOnly("Downloading")} aria-label="Download">
            <DownloadLargeIcon />
          </button>
          <button
            type="button"
            className="sp-action"
            onClick={() => spotifyOnly("That menu")}
            aria-label="More options"
          >
            <MoreLargeIcon />
          </button>
          <span className="sp-view" aria-hidden="true">
            List
            <ListIcon />
          </span>
        </div>

        <div className={`sp-tracks ${showPlays ? "has-plays" : ""}`} role="table" aria-label={list.name}>
          <div className={`sp-track sp-track--head ${headStuck ? "is-stuck" : ""}`} role="row" ref={head}>
            <span className="sp-track-index" role="columnheader">
              #
            </span>
            <span role="columnheader">Title</span>
            {showPlays && (
              <span className="sp-track-plays" role="columnheader">
                Plays
              </span>
            )}
            <span className="sp-track-end" role="columnheader" aria-label="Duration">
              <ClockIcon className="sp-clock" />
            </span>
          </div>
          {list.tracks.map((track, i) => (
            <TrackRow
              key={track.title}
              track={track}
              index={i}
              current={isPlayingFrom && playingIndex === i}
              playing={playing}
              selected={selected === i}
              showPlays={showPlays}
              onSelect={() => setSelected(i)}
              onPlay={() => playTrack(i)}
            />
          ))}
        </div>

        {list.released && (
          <footer className="sp-release">
            <div>
              <p className="sp-release-date">{list.released}</p>
              {list.copyright.map((line) => (
                <p key={line} className="sp-release-copy">
                  {line}
                </p>
              ))}
            </div>
            <span className="sp-more-releases" aria-hidden="true">
              1 more release
              <CaretDownIcon />
            </span>
          </footer>
        )}

        {list.artist && (
          <section className="sp-more" aria-label={`More by ${list.artist}`}>
            <div className="sp-more-head">
              <h2>More by {list.artist}</h2>
              <span aria-hidden="true">See discography</span>
            </div>
            <div className="sp-more-grid">
              {MORE_BY.map((album) => (
                <button
                  type="button"
                  key={album.title}
                  className="sp-card"
                  onClick={() => notify(`${album.title} plays in Spotify itself. ICEMAN and Liked Songs play here.`)}
                >
                  <img src={album.image} alt="" draggable={false} />
                  <span className="sp-card-title">{album.title}</span>
                  <span className="sp-card-sub">{album.year} • Album</span>
                </button>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default PlaylistPage;
